import { Grid } from '@mui/material';
import { BarSeriesType } from '@mui/x-charts';
import produce from 'immer';
import React from 'react';
import { useTranslation } from 'react-i18next';
import { UNDEFINED_DISPLAY_KEY } from '../../../../constants';
import { useGlobalLocaleContext } from '../../../../context/contexts';
import { DataFieldWithDataType } from '../../../../types';
import { transformToTranslate } from '../../../../utils-display';
import { CHART_CONTENT_HEIGHT, CHART_TITLE_HEIGHT } from '../../../styles';
import {
  LineChartComponentConfig,
  OverTimeBarChartComponentConfig,
} from '../../dashboards/types';
import { ChartErrorDisplay } from '../../errors/components/ChartErrorDisplay';
import { formatHierarchicalLabel } from '../../filter/utils-display';
import { TableViewMetrics } from '../../tableview/TableViewMetrics';
import { ChartComponentProps } from '../chart-types';
import { ChartDataFetchError } from '../ChartDataFetchError';
import { ChartTitle } from '../ChartTitle';
import {
  useChartWidth,
  useSyncGlobalDisplayState,
  useToggleToolsOnSegmentationChange,
} from '../hooks';
import { BarChart } from '../mui-charts';
import { toMuiChartsOverTimeData } from '../mui-charts/utils-display';
import { useMetricQueries } from './hooks';
import { Tools } from './Tools';
import { getSeriesStyles } from './utils';

type BarChartOverTimeProps = ChartComponentProps<
  LineChartComponentConfig | OverTimeBarChartComponentConfig,
  | 'manualDataRefetchCountHandle'
  | 'timeSliderState'
  | 'filtersState'
  | 'segmentationState'
  | 'showTools'
  | 'fullScreenHandle'
  | 'displayGlobalState'
>;

export const BarChartOverTime = (props: BarChartOverTimeProps) => {
  const {
    componentConfig,
    manualDataRefetchCountHandle,
    timeSliderState,
    filtersState,
    segmentationState,
    showTools,
    fullScreenHandle,
    displayGlobalState,
  } = props;

  const { title, queries, chartTypeConfig, colors } = componentConfig;
  const locale = useGlobalLocaleContext();
  const { t } = useTranslation();
  const formatLabel = (
    value: (string | null)[],
    dataField: DataFieldWithDataType
  ) => formatHierarchicalLabel(value, dataField, t(UNDEFINED_DISPLAY_KEY), t);

  const { chartContainerRef, chartWidth } = useChartWidth();

  const {
    data: {
      series,
      tableViewData,
      barLabelConfig,
      metricSql,
      xAxisConfig,
      yAxisConfig,
    },
    displayHandle,
    metrics,
    filters,
    segmentations,
    timeSelection,
    error,
  } = useMetricQueries<BarSeriesType>(
    componentConfig.chartId,
    queries,
    timeSliderState,
    filtersState,
    segmentationState,
    manualDataRefetchCountHandle.current,
    chartTypeConfig,
    displayGlobalState,
    formatLabel,
    colors,
    t,
    toMuiChartsOverTimeData<BarSeriesType>,
    chartWidth
  );

  const [displayState, displayReducer] = displayHandle;

  useSyncGlobalDisplayState(displayGlobalState, displayReducer);
  useToggleToolsOnSegmentationChange(
    segmentations,
    segmentationState,
    displayReducer
  );

  if (error && error.length > 0) {
    return (
      <ChartErrorDisplay
        error={error[0]}
        variant="inline"
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  // TODO: missing xAxisConfig and yAxisConfig would benefit from a dedicated error handling
  if (!xAxisConfig || !yAxisConfig) {
    return (
      <ChartDataFetchError
        retry={() => manualDataRefetchCountHandle.increment()}
      />
    );
  }

  const styles = getSeriesStyles(queries, series);

  const [firstAxis, ...otherXAxis] = xAxisConfig;
  const bottomXAxis = produce(firstAxis, (draft) => {
    draft.tickLabelStyle = {
      transform: firstAxis.transform
        ? transformToTranslate(firstAxis.transform)
        : undefined,
    };
  });
  const finalXAxisConfig = [bottomXAxis, ...otherXAxis];

  return (
    <Grid item container direction="column" sx={{ height: '100%' }}>
      <Grid item container sx={{ height: CHART_TITLE_HEIGHT }}>
        <ChartTitle
          metrics={metrics}
          metricsSql={metricSql}
          title={title?.[locale.selected]}
          timeSelection={timeSelection}
          segmentations={segmentations}
          userFilters={filters}
        />
        {showTools ? (
          <Tools
            chartRef={chartContainerRef}
            displayHandle={displayHandle}
            fullScreenHandle={fullScreenHandle}
          />
        ) : null}
      </Grid>
      <Grid
        item
        container
        ref={chartContainerRef}
        sx={{ height: CHART_CONTENT_HEIGHT }}
      >
        {displayState.showTableView ? (
          <TableViewMetrics
            tableData={tableViewData}
            fullScreenHandle={fullScreenHandle}
            chartHeight={CHART_CONTENT_HEIGHT}
          />
        ) : (
          <BarChart
            sx={styles}
            title={title?.[locale.selected]}
            xAxis={finalXAxisConfig.map((axis) => ({
              ...axis,
              scaleType: 'band',
            }))}
            yAxis={yAxisConfig}
            series={series}
            margin={{
              right: 50,
              left: 50,
              top: 40,
              bottom: bottomXAxis.height ?? 0,
            }}
            slotProps={{
              popper: { container: () => fullScreenHandle.node.current },
              legend: {
                hidden: !displayState.showLegend,
                direction: 'column',
                position: {
                  vertical: 'top',
                  horizontal: 'right',
                },
                padding: 50,
                itemMarkWidth: 20,
                itemMarkHeight: 2,
                markGap: 5,
                itemGap: 5,
                labelStyle: {
                  fontSize: 12,
                },
              },
              noDataOverlay: { message: t('common:customDashboard.noData') },
            }}
            barLabel={barLabelConfig}
            grid={{
              vertical: displayGlobalState.showGridLines,
              horizontal: displayGlobalState.showGridLines,
            }}
            rightAxis={(yAxisConfig?.[1]?.id as string) ?? undefined}
            tooltip={{ trigger: 'axis' }}
          />
        )}
      </Grid>
    </Grid>
  );
};
